/**
 * Sanitization utilities to prevent XSS attacks
 * Uses dynamic imports for DOMPurify to optimize bundle size
 */

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [] as string[],
  ALLOWED_ATTR: [] as string[],
};

/**
 * Native sanitization for simple cases - removes all HTML tags
 * Use this for server components or when you don't need complex sanitization
 * @param value - The string to sanitize
 * @returns The sanitized string with HTML tags removed
 */
export function sanitizeNative(value: string): string {
  if (!value || typeof value !== "string") return value;
  return value.replace(/<[^>]*>/g, "");
}

/**
 * Dynamic DOMPurify for complex cases - strips all HTML tags and attributes
 * Use this for client components when dynamic import is acceptable
 * @param value - The string to sanitize
 * @returns Promise resolving to the sanitized string
 */
export async function sanitizeWithDOMPurify(value: string): Promise<string> {
  if (!value || typeof value !== "string") return value;
  const DOMPurify = await import("isomorphic-dompurify");
  return DOMPurify.default.sanitize(value, SANITIZE_CONFIG);
}

/**
 * Server-side sanitization with specific allowed tags
 * For server components that need to preserve safe HTML
 * @param html - The HTML string to sanitize
 * @param allowedTags - Array of allowed HTML tags
 * @param allowedAttr - Array of allowed HTML attributes
 * @returns The sanitized HTML string
 */
export function sanitizeServerSide(
  html: string,
  allowedTags: string[] = [
    "p",
    "br",
    "strong",
    "em",
    "ul",
    "ol",
    "li",
    "h1",
    "h2",
    "h3",
    "a",
    "code",
    "pre",
  ],
  allowedAttr: string[] = ["href"],
): string {
  if (!html || typeof html !== "string") return html;
  // Simple tag stripping with allowed list - for server components
  // Note: For production, consider using a proper HTML parser like sanitize-html
  const tagRegex = new RegExp(
    `<(?!/?(?:${allowedTags.join("|")})\b)[^>]*>`,
    "gi",
  );
  const attrRegex = new RegExp(
    `\s+(?!${allowedAttr.join("|")}\b)[\w-]+="[^"]*"`,
    "gi",
  );

  let sanitized = html.replace(tagRegex, "");
  sanitized = sanitized.replace(attrRegex, "");
  return sanitized;
}

// Re-export config for backward compatibility
export { SANITIZE_CONFIG };
